import { LitElement, html, css, t, paths, unsafeHTML } from '@hh/global.js';

class HHBundleProduct extends LitElement {
  static properties = {
    product: { type: Object },
    currentStep: { type: Number },
    maxStepQty: { type: Number },
    bundle: {
      type: Array,
    },
    qty: {
      state: true,
    },
    isDisabled: {
      type: Boolean
    },
    type: {
      type: Number
    }
  }

  constructor() {
    super();
    this.qty = 1;
  }

  updated(changedProperties) {
    if (changedProperties.has('currentStep')) {
      this.qty = this.initQty;
    }

    if (changedProperties.has('bundle')) {
      if (!this.isAdded) {
        this.qty = 1;
      }
    }
  }

  updateBundle(qty) {
    this.dispatchEvent(new CustomEvent('updateBundle', {
      detail: {
        variantId: this.product.variantId,
        qty: qty,
      },
      bubbles: true,
      composed: true
    }));
  }

  viewProduct() {
    this.dispatchEvent(new CustomEvent('previewProduct', {
      detail: {
        product: this.product,
      },
      bubbles: true,
      composed: true
    }));
  }

  onChange(event) {
    this.qty = event.detail.value;
    if (this.isAdded) {
      this.updateBundle(this.qty);
    }
  }

  onAdd() {
    this.updateBundle(this.qty);
  }

  onRemove() {
    this.updateBundle(0);
  }

  get isAdded() {
    return this.bundle[this.currentStep].some((item) => item.variantId === this.product.variantId);
  }

  get initQty() {
    return this.bundle[this.currentStep].find((item) => item.variantId === this.product.variantId)?.qty || 1;
  }

  get maxQty() {
    const maxQty = this.maxStepQty - this.bundle[this.currentStep].reduce((acc, item) => {
      if (item.variantId !== this.product.variantId) {
        acc += item.qty;
      }
      return acc;
    }, 0) || 1;
    return Math.min(maxQty, this.product.stockQty)
  }

  render() {
    if (!this.product?.available) {
      return html`
        <div class="bundle-product bundle-product--unavailable">
          <img class="bundle-product__image" src="${this.product.image}" alt="${this.product.title}" />
          <div class="bundle-product__title">${this.product.title}</div>
          <div class="bundle-product__description">${this.product.annotation}</div>
          <div class="bundle-product__unavailable-label">Not available</div>
        </div>
      `;
    }
    return html`
    <div class="bundle-product ${this.isAdded ? 'active' : ''}">
      ${this.isDisabled && !this.isAdded ? html`<div class="bundle-product__overlay"></div>` : ''}
      ${this.product.badge ? unsafeHTML(this.product.badge) : ''}
      <img class="bundle-product__image" src="${this.product.image}" alt="${this.product.title}" />
      <div>
        <div class="bundle-product__state">
          ${this.isAdded ? html`
            ${unsafeHTML(paths.checkmark)}
            ${t('products.product.added_to_cart')}
          ` : ''}
        </div>
        <div class="bundle-product__title">${this.product.title}</div>
        <button class="bundle-product__details" .disabled="${this.isDisabled && !this.isAdded}" @click="${this.viewProduct}">
          ${t('sections.main-bundle.view_details')}
        </button>
        <hh-quantity-input
          .initial='${this.initQty}'
          min='1'
          .max="${this.maxQty}"
          .key="${this.product.variantId}"
          .disabled="${this.isDisabled && !this.isAdded}"
          @changeQuantity="${this.onChange}"
        >
          <button .disabled="${this.isDisabled && !this.isAdded}" type='button' name='minus' slot='decrease'>
            ${unsafeHTML(paths.minus)}
          </button>
          <button .disabled="${this.isDisabled && !this.isAdded}" type='button' name='plus' slot='increase'>
            ${unsafeHTML(paths.plus)}
          </button>
        </hh-quantity-input>
        <div class="bundle-product__bottom">
          <div>
            <div class="bundle-product__sku">
              ${this.product.catalog_number}
            </div>
            ${this.type > 0 ? html`
            <div class="bundle-product__price">
              ${this.product.sale_price ? html`
                <div class="price--sale">${this.product.price}</div>
                <div class="price">${this.product.sale_price}</div>
              ` : html`
                <div class="price--regular">${this.product.price}</div>
              `}
            </div>`
        : ""}
          </div>
          <div>
            ${this.isAdded ?
        html`<button class="bundle-product__button" @click="${this.onRemove}">
              ${unsafeHTML(paths.trash)}
            </button>` :
        html`<button class="bundle-product__button" @click="${this.onAdd}" .disabled="${this.isDisabled}">
                ${unsafeHTML(paths.plus)}
              </button>`}
          </div>
        </div>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
    }
    .bundle-product{
      border-radius: 0.5rem;
      border: 0.3rem solid var(--hh-color-day);
      padding: var(--hh-spacing-sm);
      background: var(--hh-color-day);
      display: flex;
      flex-direction: column;
      gap: var(--hh-spacing-sm);
      overflow: hidden;
      position: relative;
      flex: 1;
    }
    .bundle-product.active {
      border-color: var(--hh-color-brand-secondary);
    }
    .bundle-product hh-quantity-input {
      margin-bottom: var(--hh-spacing-sm);
      display: block;
    }
    .bundle-product hh-quantity-input .icon {
      height: 1.6rem;
      width: 1.6rem;
    }
    .bundle-product__overlay {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: rgba(var(--hh-color-day-rgb), 0.75);
      z-index: 10;
    }
    .bundle-product__image {
      width: 100%;
      height: auto;
      display: block;
      margin-bottom: auto;
    }
    .bundle-product__title {
      font: var(--hh-body-bold-sm-font);
      letter-spacing: var(--hh-body-bold-sm-letter-spacing);
      color: var(--hh-color-night);
      margin-bottom: var(--hh-spacing-xxs);
    }
    .bundle-product__details {
      display: block;
      font: var(--hh-body-bold-sm-font);
      letter-spacing: var(--hh-body-bold-sm-letter-spacing);
      color: var(--hh-color-brand-secondary);
      text-decoration: underline;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      margin-bottom: var(--hh-spacing-sm);
    }
    .bundle-product__sku {
      font: var(--hh-body-xxs-font);
      letter-spacing: var(--hh-body-sm-letter-spacing);
    }
    .bundle-product__description {
      font: var(--hh-body-xs-font);
      letter-spacing: var(--hh-body-xs-letter-spacing);
      margin-bottom: var(--hh-spacing-xxs);
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .bundle-product__price{
      display: flex;
      gap: var(--hh-spacing-xs);
      font: var(--hh-body-sm-font);
      letter-spacing: var(--hh-body-sm-letter-spacing);
      flex-direction: row-reverse;
    }
    .price{
      color: var(--hh-color-brand-primary);
      font-weight: bold;
    }
    .price--sale{
      text-decoration: line-through;
    }
    .bundle-product__bottom{
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
    }
    .bundle-product__button{
      width: 3.9rem;
      height: 3.9rem;
      border-radius: 50%;
      background: var(--hh-color-brand-primary);
      border: none;
      color: var(--hh-color-day);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .bundle-product__button svg{
      height: 1.8rem;
      width: auto;
    }
    .bundle-product__state{
      font: var(--hh-body-bold-sm-font);
      letter-spacing: var(--hh-body-bold-sm-letter-spacing);
      color: var(--hh-color-success);
      display: flex;
      align-items: center;
      gap: var(--hh-spacing-xxs);
      min-height: 1.82rem;
      margin-bottom: var(--hh-spacing-xs);
    }
    .bundle-product__state svg{
      height: 0.6rem;
      width: auto;
    }

    @media (min-width: 1024px) {
      .bundle-product__title {
        font: var(--hh-h4-font);
        margin-bottom: var(--hh-spacing-xs);
      }
      .bundle-product__description {
        margin-bottom: var(--hh-spacing-xs);
      }
      .bundle-product hh-quantity-input{
        margin-bottom: var(--hh-spacing-lg);
      }
      .bundle-product__details {
        margin-bottom: var(--hh-spacing-lg);
      }
    }
    .bundle-product--unavailable {
      opacity: 0.5;
      pointer-events: none;
      position: relative;
    }
    .bundle-product__unavailable-label {
      color: var(--hh-color-danger, #f00);
      font: var(--hh-body-bold-sm-font);
      text-align: center;
      margin-top: 1rem;
    }
  `
}

if (!customElements.get('hh-bundle-product')) {
  customElements.define('hh-bundle-product', HHBundleProduct);
}
